// Pricing sheets awaiting review (Phase 4 slice 2b).
//
// §0 — the tab's one question: "which uploaded sheets still need a decision from
// me?" Primary object: a collection. Primary verb: open one. Worst case: a few
// dozen staged sheets per tenant.
//
// Archetype B (collection): title + count, a table, row click opens the record.
// No side panel here — a review is a full page, because confirming a reading and
// ruling on hundreds of blocks does not fit in a drawer.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, SpecraBadge, SpecraButton, SpecraDataTable, SpecraEntityCell, SpecraSectionHeader, SpecraSwitchField, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RegistryListNote } from '../../Registries/list/RegistryListNote';
import { useShowMore } from '../../Registries/list/useShowMore';
import { useRegistryRecordsEnabled } from '../../Registries/registryFlags';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { useOwnCompanyProfile } from '../Programs/referenceDataHooks';
import { CalcPlusRefreshDialog, useCanRefreshCalcPlus } from './CalcPlusRefreshDialog';
import { usePricingSheetReviews } from './hooks';
import { PricingSheetIngestProgress } from './PricingSheetIngestProgress';
import { PricingSheetUploadDialog } from './PricingSheetUploadDialog';
import { useActiveIngests } from './useActiveIngests';
import { useCanUploadPricingSheet } from './usePricingSheetUploadGate';
function formatWhen(ts) {
    if (!ts?.seconds) {
        return '—';
    }
    return new Date(Number(ts.seconds) * 1000).toLocaleDateString();
}
export function PricingSheetsTab() {
    const recordsEnabled = useRegistryRecordsEnabled();
    const ownCompany = useOwnCompanyProfile({
        enabled: recordsEnabled
    }).data;
    const companyId = useTenantId();
    const navigate = useNavigate();
    const canUpload = useCanUploadPricingSheet();
    // A LIST, not one slot: uploading a second sheet used to overwrite the first
    // and stop watching it.
    const { runs: activeRuns, track, untrack } = useActiveIngests(companyId);
    // Finished reviews are opt-in: the default question is "what is waiting on
    // me", and committed sheets would otherwise pile up above the open ones.
    const [includeApplied, setIncludeApplied] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [refreshOpen, setRefreshOpen] = useState(false);
    const canRefresh = useCanRefreshCalcPlus();
    const { data, isLoading, isError, refetch } = usePricingSheetReviews(companyId, {
        includeApplied
    });
    // The server hides applied sheets unless asked; History is applied, so guard it here too.
    const reviews = (data ?? []).filter((r)=>includeApplied || r.listStatus !== 'History');
    const paging = useShowMore(reviews, String(includeApplied));
    const chip = (r)=>{
        switch(r.listStatus){
            case 'History':
                return [
                    'neutral',
                    'History'
                ];
            case 'Committed':
                return [
                    'success',
                    'Committed'
                ];
            case 'AwaitingDecision':
                return [
                    'warning',
                    `Awaiting decision (${r.awaitingDecisions})`
                ];
            case 'Parsed':
            case 'Rejected':
            case 'Cancelled':
                return [
                    'neutral',
                    r.listStatus
                ];
        }
        if (r.supersededById || r.superseded || r.state === 'Superseded') return [
            'neutral',
            'History'
        ];
        if (r.state === 'Applied') return [
            'success',
            'Committed'
        ];
        if ([
            'Rejected',
            'Cancelled'
        ].includes(r.state)) return [
            'neutral',
            r.state
        ];
        return r.openBlocks > 0 ? [
            'warning',
            `${r.openBlocks} awaiting a decision`
        ] : [
            'warning',
            'Ready to commit'
        ];
    };
    const status = (r)=>/*#__PURE__*/ _jsxs("div", {
            className: "flex flex-wrap gap-2",
            children: [
                /*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: chip(r)[0],
                    children: chip(r)[1]
                }),
                r.pendingPeriodDecisions > 0 && /*#__PURE__*/ _jsxs(SpecraBadge, {
                    variant: "warning",
                    children: [
                        r.pendingPeriodDecisions,
                        " ",
                        r.pendingPeriodDecisions === 1 ? 'period' : 'periods',
                        " to confirm"
                    ]
                })
            ]
        });
    return /*#__PURE__*/ _jsxs("section", {
        children: [
            activeRuns.length > 0 ? /*#__PURE__*/ _jsx("div", {
                className: "mb-4 space-y-2",
                children: activeRuns.map((r)=>/*#__PURE__*/ _jsx(PricingSheetIngestProgress, {
                        filename: r.filename,
                        onSettled: ()=>{
                            untrack(r.runId);
                            void refetch();
                        },
                        runId: r.runId
                    }, r.runId))
            }) : null,
            /*#__PURE__*/ _jsx(PricingSheetUploadDialog, {
                onOpenChange: setUploadOpen,
                // Tracked, not navigated: several sheets can be in flight and each keeps
                // its own card.
                onStarted: (id, filename)=>track(id, filename),
                open: uploadOpen
            }),
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                action: /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center gap-3",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraSwitchField, {
                            controlProps: {
                                checked: includeApplied,
                                onCheckedChange: setIncludeApplied
                            },
                            label: "Show committed"
                        }),
                        canUpload && /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": Testids.pricingSheetReview.uploadOpen,
                            onClick: ()=>setUploadOpen(true),
                            children: "Upload Pricing Sheet"
                        }),
                        canRefresh && /*#__PURE__*/ _jsxs(DropdownMenu, {
                            children: [
                                /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                                    asChild: true,
                                    children: /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "ghost",
                                        size: "sm",
                                        "aria-label": "Pricing sheet actions",
                                        children: "⋯"
                                    })
                                }),
                                /*#__PURE__*/ _jsx(DropdownMenuContent, {
                                    align: "end",
                                    children: /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                        "data-testid": Testids.deliveryRegistries.calcPlusRefresh,
                                        onSelect: ()=>setRefreshOpen(true),
                                        children: "Refresh CALC+ benchmarks…"
                                    })
                                })
                            ]
                        })
                    ]
                }),
                headingAs: "h2",
                title: `Pricing Sheets (${reviews.length})`
            }),
            canRefresh && refreshOpen && /*#__PURE__*/ _jsx(CalcPlusRefreshDialog, {
                onOpenChange: setRefreshOpen
            }),
            recordsEnabled && isError && /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                role: "alert",
                children: "Unable to load pricing sheets."
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mt-3",
                children: /*#__PURE__*/ _jsx(SpecraDataTable, {
                    columns: recordsEnabled ? [
                        {
                            id: 'title',
                            header: 'Sheet',
                            cell: (r)=>/*#__PURE__*/ _jsx(SpecraEntityCell, {
                                    title: r.title.replace(/^pricing sheet:\s*/i, '') || r.sourceFilename || '(untitled sheet)',
                                    description: r.companyCount > 1 ? `one upload, ${r.companyCount} companies` : undefined
                                })
                        },
                        {
                            id: 'issuer',
                            header: 'Issued by',
                            cell: (r)=>r.issuerKind === 'GOVERNMENT' ? 'Government' : r.companyProfileId && r.companyProfileId === ownCompany?.id ? 'Us' : r.companyProfileName || '—'
                        },
                        {
                            id: 'vehicle',
                            header: 'Vehicle',
                            cell: (r)=>r.contractVehicleName || '—'
                        },
                        {
                            id: 'generation',
                            header: 'Generation',
                            cell: (r)=>r.generationLabel || '—'
                        },
                        {
                            id: 'rows',
                            header: 'Rows',
                            align: 'right',
                            cell: (r)=>r.rowCount > 0 ? r.rowCount.toLocaleString() : '—'
                        },
                        {
                            id: 'state',
                            header: 'Status',
                            cell: status
                        }
                    ] : [
                        {
                            id: 'title',
                            header: 'Sheet',
                            cell: (r)=>r.title || '(untitled sheet)'
                        },
                        {
                            id: 'state',
                            header: 'Status',
                            cell: (r)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: r.state === 'Applied' ? 'success' : r.blockingBlocks > 0 ? 'danger' : r.openBlocks > 0 ? 'warning' : 'neutral',
                                    children: r.state === 'Applied' ? 'Committed' : r.openBlocks > 0 ? `${r.openBlocks} awaiting a decision` : 'Ready to commit'
                                })
                        },
                        {
                            id: 'staged_by',
                            header: 'Staged by',
                            cell: (r)=>r.createdBy || '—'
                        },
                        {
                            align: 'right',
                            id: 'rates',
                            header: 'Rates',
                            cell: (r)=>r.rates.toLocaleString()
                        },
                        {
                            align: 'right',
                            id: 'blocks',
                            header: 'Blocks',
                            cell: (r)=>String(r.blocks)
                        },
                        {
                            align: 'right',
                            id: 'staged',
                            header: 'Staged',
                            cell: (r)=>formatWhen(r.createdAt)
                        }
                    ],
                    "data-testid": Testids.pricingSheetReview.list,
                    // What is missing, why, and one button.
                    emptyAction: /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>navigate('/registries?tab=vehicles'),
                        variant: "outline",
                        children: "Go to contract vehicles"
                    }),
                    emptyDescription: "A pricing sheet appears here once it has been uploaded against a contract vehicle and parsed. Nothing reaches the rate book until a sheet is reviewed and committed.",
                    emptyTitle: "No pricing sheets are waiting",
                    getRowActionLabel: (r)=>`Review ${r.rates.toLocaleString()} staged rates from ${r.title || 'this sheet'}`,
                    getRowId: (r)=>r.changeSetId,
                    isLoading: isLoading,
                    onRowClick: (r)=>navigate(`/registries/pricing-sheets/${r.changeSetId}`),
                    rowTestId: (r)=>`${Testids.pricingSheetReview.listRow}-${r.changeSetId}`,
                    rows: recordsEnabled ? paging.visible : reviews
                })
            }),
            recordsEnabled && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    paging.hasMore && /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        "data-testid": Testids.deliveryRegistries.showMore,
                        onClick: paging.showMore,
                        children: "Show more"
                    }),
                    /*#__PURE__*/ _jsx(RegistryListNote, {
                        children: "Every generation is kept. A new sheet adds observations; it never overwrites old ones."
                    })
                ]
            })
        ]
    });
}
